import { useMemo } from "react";
import { useSelector } from "react-redux";
import { AppState } from "../store/rootReducer";
import { hasValue } from "@amsanalytics/react-shared-library";

interface AllowedQueueNavigationStatusesResult {
  allowedStatuses: string[];
  isRestricted: boolean;
}

/**
 * Hook that reads AllowedQueueNavigationStatuses from the logged-in user's
 * employee role ConfigurationJson.
 *
 * Returns merged status restrictions across all roles.
 * If any role has no AllowedQueueNavigationStatuses restriction, all navigation is allowed.
 */
export const useAllowedQueueNavigationStatuses = (): AllowedQueueNavigationStatusesResult => {
  const allEmployeeRoles = useSelector((appState: AppState) => appState.applications.employeeRoles);
  const loggedInUser = useSelector((appState: AppState) => appState.applications.loggedInUser);
  const employeeRoleIds = loggedInUser?.employee?.employeeEmployeeRoles?.map(eer => eer.employeeRoleId) ?? [];

  return useMemo(() => {
    const statusSet = new Set<string>();
    let hasUnrestrictedRole = false;
    let hasAtLeastOneRestrictedRole = false;

    if (allEmployeeRoles) {
      const userRoles = allEmployeeRoles.filter(er => employeeRoleIds.includes(er.id));
      userRoles.forEach(role => {
        if (!hasValue(role.configurationJson)) {
          hasUnrestrictedRole = true;
          return;
        }

        try {
          const config = JSON.parse(role.configurationJson);
          const allowed: string[] | undefined = config.AllowedQueueNavigationStatuses;

          if (Array.isArray(allowed) && allowed.length > 0) {
            hasAtLeastOneRestrictedRole = true;
            allowed.forEach(s => statusSet.add(s.toUpperCase()));
          } else {
            hasUnrestrictedRole = true;
          }
        } catch {
          // Invalid JSON - treat as unrestricted rather than unexpectedly blocking navigation.
          hasUnrestrictedRole = true;
        }
      });
    }

    if (hasUnrestrictedRole || !hasAtLeastOneRestrictedRole) {
      return { allowedStatuses: [], isRestricted: false };
    }

    return { allowedStatuses: Array.from(statusSet), isRestricted: true };
  }, [allEmployeeRoles, employeeRoleIds]);
};
